"use client";

import { SiteFooter } from "../components/SiteFooter";
import { SiteHeader } from "../components/SiteHeader";
import { ThemeProvider } from "../theme/ThemeProvider";
import type { ReactNode } from "react";

/**
 * The page chrome (`WEB-044`, canon decision `D-020`).
 *
 * What `App.tsx` was, minus the routing. Under the app router each route is its own document,
 * so the shell no longer reads `window.location` to decide which page it is showing — the
 * page decides that by existing, and the branch that used to make that decision is gone
 * rather than reimplemented.
 *
 * **This is the client boundary for the whole site.** Everything below it — the board, the
 * auction, the theme provider — is a client component by inheritance, and none of them needed
 * a directive of their own. That is deliberate: one boundary is one thing to reason about,
 * and scattering `"use client"` through thirty components would make the line between build
 * and browser something you have to reconstruct file by file.
 *
 * Being a client component does **not** make it a client-rendered page. Next prerenders these
 * to HTML at build time, which is the entire point of `D-020`: the markup ships complete and
 * hydration only makes it interactive.
 */
export function Shell({
  honorableMentions,
  children,
}: {
  /**
   * Whether the masthead offers the Honorable Mentions link (`WEB-024`).
   *
   * The homepage decides it from the board it was built with. Every other route passes
   * `true`: it did not look, and a route that has not looked must not retract a destination
   * on that basis. `SiteHeader` documents the whole rule.
   */
  honorableMentions: boolean;
  children: ReactNode;
}) {
  return (
    <ThemeProvider>
      <SiteHeader honorableMentions={honorableMentions} />
      {children}
      <SiteFooter />
    </ThemeProvider>
  );
}
